import Phaser from 'phaser';
import type { Lane } from '@/types';
import { CAT_BODY_HALF_WIDTH, CAT_KEYBOARD_SPEED, INTERNAL_WIDTH } from '@/game/config/GameConfig';
import { clamp } from '@/utils/rhythmMath';

const LANE_WIDTH_PX = INTERNAL_WIDTH / 2;
const MIN_X = CAT_BODY_HALF_WIDTH;
const MAX_X = 1 - CAT_BODY_HALF_WIDTH;

/**
 * Translates raw pointer/touch/keyboard input into a target normalized X (0..1,
 * clamped to the cat's movable range) per lane. A pointer is "claimed" by whichever
 * lane it first touches down in and keeps controlling that lane, even if the finger
 * drifts across the midline, until it's released -- this is what stops fingers
 * crossing near the center from swapping which cat they control.
 */
export class InputManager {
  private leftTargetX = 0.5;
  private rightTargetX = 0.5;
  private leftPointerId: number | null = null;
  private rightPointerId: number | null = null;

  private keys: {
    a: Phaser.Input.Keyboard.Key;
    d: Phaser.Input.Keyboard.Key;
    left: Phaser.Input.Keyboard.Key;
    right: Phaser.Input.Keyboard.Key;
  } | null = null;

  private onDown = (pointer: Phaser.Input.Pointer) => this.handleDown(pointer);
  private onMove = (pointer: Phaser.Input.Pointer) => this.handleMove(pointer);
  private onUp = (pointer: Phaser.Input.Pointer) => this.handleUp(pointer);

  constructor(private scene: Phaser.Scene) {
    scene.input.addPointer(3); // total simultaneous pointers = mouse + 3 touches
    scene.input.on('pointerdown', this.onDown);
    scene.input.on('pointermove', this.onMove);
    scene.input.on('pointerup', this.onUp);
    scene.input.on('pointerupoutside', this.onUp);

    if (scene.input.keyboard) {
      this.keys = {
        a: scene.input.keyboard.addKey('A'),
        d: scene.input.keyboard.addKey('D'),
        left: scene.input.keyboard.addKey('LEFT'),
        right: scene.input.keyboard.addKey('RIGHT'),
      };
    }
  }

  private laneForX(x: number): Lane {
    return x < LANE_WIDTH_PX ? 'left' : 'right';
  }

  private normalizedXForLane(x: number, lane: Lane): number {
    const laneOffset = lane === 'left' ? 0 : LANE_WIDTH_PX;
    const raw = (x - laneOffset) / LANE_WIDTH_PX;
    return clamp(raw, MIN_X, MAX_X);
  }

  private handleDown(pointer: Phaser.Input.Pointer): void {
    const lane = this.laneForX(pointer.x);
    if (lane === 'left' && this.leftPointerId === null) {
      this.leftPointerId = pointer.id;
      this.leftTargetX = this.normalizedXForLane(pointer.x, 'left');
    } else if (lane === 'right' && this.rightPointerId === null) {
      this.rightPointerId = pointer.id;
      this.rightTargetX = this.normalizedXForLane(pointer.x, 'right');
    }
  }

  private handleMove(pointer: Phaser.Input.Pointer): void {
    if (!pointer.isDown) return;
    if (pointer.id === this.leftPointerId) {
      this.leftTargetX = this.normalizedXForLane(pointer.x, 'left');
    } else if (pointer.id === this.rightPointerId) {
      this.rightTargetX = this.normalizedXForLane(pointer.x, 'right');
    }
  }

  private handleUp(pointer: Phaser.Input.Pointer): void {
    if (pointer.id === this.leftPointerId) this.leftPointerId = null;
    if (pointer.id === this.rightPointerId) this.rightPointerId = null;
  }

  /** Call once per frame with delta time in seconds to apply smooth keyboard movement. */
  update(deltaSeconds: number): void {
    if (!this.keys) return;
    if (this.leftPointerId === null) {
      if (this.keys.a.isDown) this.leftTargetX -= CAT_KEYBOARD_SPEED * deltaSeconds;
      if (this.keys.d.isDown) this.leftTargetX += CAT_KEYBOARD_SPEED * deltaSeconds;
      this.leftTargetX = clamp(this.leftTargetX, MIN_X, MAX_X);
    }
    if (this.rightPointerId === null) {
      if (this.keys.left.isDown) this.rightTargetX -= CAT_KEYBOARD_SPEED * deltaSeconds;
      if (this.keys.right.isDown) this.rightTargetX += CAT_KEYBOARD_SPEED * deltaSeconds;
      this.rightTargetX = clamp(this.rightTargetX, MIN_X, MAX_X);
    }
  }

  getTargetX(lane: Lane): number {
    return lane === 'left' ? this.leftTargetX : this.rightTargetX;
  }

  destroy(): void {
    this.scene.input.off('pointerdown', this.onDown);
    this.scene.input.off('pointermove', this.onMove);
    this.scene.input.off('pointerup', this.onUp);
    this.scene.input.off('pointerupoutside', this.onUp);
  }
}
